import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';
import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	Store,
	StoreActionApi,
} from 'react-sweet-state';
import isEqual from 'lodash/isEqual';

export enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

export enum CommentActionType {
	DELETE_COMMENT = 'deleted', // If you ever update this, please also update commentsNavigationUtils, which relies on the string to avoid circular deps
	RESOLVE_COMMENT_THREAD = 'resolved',
}

export interface AnnotationStatus {
	threadKey: string;
	isLoaded: boolean;
}

export type RemovedCommentsMap = Record<
	string,
	CommentActionType.DELETE_COMMENT | CommentActionType.RESOLVE_COMMENT_THREAD
>;

export type CommentsDataState = {
	orderedActiveAnnotationIdList: AnnotationStatus[]; // ordered list of annotations on the page
	commentThreadsRemovedByOtherUsers: RemovedCommentsMap; // parent comment ID -> action type ('deleted' or 'resolved')
};

export const getInitialState = (): CommentsDataState => ({
	orderedActiveAnnotationIdList: [],
	commentThreadsRemovedByOtherUsers: {},
});

export const actions = {
	setOrderedActiveAnnotationIdList:
		(newCommentsList: string[]) =>
		({ getState, setState }: StoreActionApi<CommentsDataState>): void => {
			const { orderedActiveAnnotationIdList } = getState();
			if (!newCommentsList || newCommentsList.length === 0) {
				// Only update state if the current list is not already empty
				if (orderedActiveAnnotationIdList.length > 0) {
					setState({ orderedActiveAnnotationIdList: [] });
				}
				return;
			}

			// Create a map of existing annotationId to their isLoaded states for quick lookup
			const existingAnnotationsMap = new Map(
				orderedActiveAnnotationIdList.map(({ threadKey, isLoaded }) => [threadKey, isLoaded]),
			);
			// Use a Set to ensure unique annotation IDs
			const uniqueCommentsSet = new Set(newCommentsList);
			// Map the unique comments set to the new state, preserving isLoaded where possible
			const updatedAnnotationList = Array.from(uniqueCommentsSet).map((threadKey) => ({
				threadKey,
				isLoaded: existingAnnotationsMap.get(threadKey) ?? false, // Preserve existing isLoaded or default to false
			}));

			if (!isEqual(orderedActiveAnnotationIdList, updatedAnnotationList)) {
				setState({
					orderedActiveAnnotationIdList: updatedAnnotationList,
				});
			}
		},
	getCurrentCommentsPanelState:
		() =>
		({ getState }: StoreActionApi<CommentsDataState>): Readonly<CommentsDataState> => {
			return getState();
		},
	// keep track of removed annotations
	updateRemovedCommentIdsMap:
		({
			commentId,
			commentActionType,
		}: {
			threadKey: string;
			commentId: string;
			commentActionType:
				| CommentActionType.DELETE_COMMENT
				| CommentActionType.RESOLVE_COMMENT_THREAD;
			isReply: boolean;
			commentType: CommentType;
		}) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>): void => {
			const { commentThreadsRemovedByOtherUsers } = getState();

			// Update commentThreadsRemovedByOtherUsers map
			const updatedRemovedComments = { ...commentThreadsRemovedByOtherUsers };
			updatedRemovedComments[commentId] = commentActionType;

			// Update the removed-by-other-users map.
			setState({
				commentThreadsRemovedByOtherUsers: updatedRemovedComments,
			});
		},
	reopenParentComment:
		({
			parentCommentId,
		}: {
			threadKey: string;
			commentType: CommentType;
			parentCommentId?: string;
		}) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>): void => {
			// Records a reopened parent comment in the commentThreadsRemovedByOtherUsers map.
			if (!parentCommentId) {
				return;
			}
			const { commentThreadsRemovedByOtherUsers: currentRemovedComments } = getState();
			const updatedRemovedComments = { ...currentRemovedComments };
			delete updatedRemovedComments[parentCommentId];
			setState({
				commentThreadsRemovedByOtherUsers: updatedRemovedComments,
			});
		},
	clearRemovedComments:
		() =>
		({ setState }: StoreActionApi<CommentsDataState>): void => {
			setState({
				commentThreadsRemovedByOtherUsers: {},
			});
		},
	clearCommentsData:
		() =>
		({ setState }: StoreActionApi<CommentsDataState>): void => {
			setState(getInitialState());
		},
};

export const CommentsDataStore: Store<
	CommentsDataState,
	{
		setOrderedActiveAnnotationIdList: (
			newCommentsList: string[],
		) => ({ getState, setState }: StoreActionApi<CommentsDataState>) => void;
		getCurrentCommentsPanelState: () => ({
			getState,
		}: StoreActionApi<CommentsDataState>) => Readonly<CommentsDataState>;
		// keep track of removed annotations
		updateRemovedCommentIdsMap: ({
			threadKey,
			commentId,
			commentActionType,
			isReply,
			commentType,
		}: {
			threadKey: string;
			commentId: string;
			commentActionType:
				| CommentActionType.DELETE_COMMENT
				| CommentActionType.RESOLVE_COMMENT_THREAD;
			isReply: boolean;
			commentType: CommentType;
		}) => ({ setState, getState }: StoreActionApi<CommentsDataState>) => void;
		reopenParentComment: ({
			threadKey,
			commentType,
			parentCommentId,
		}: {
			threadKey: string;
			commentType: CommentType;
			parentCommentId?: string;
		}) => ({ setState, getState }: StoreActionApi<CommentsDataState>) => void;
		clearRemovedComments: () => ({ getState, setState }: StoreActionApi<CommentsDataState>) => void;
		clearCommentsData: () => ({ setState }: StoreActionApi<CommentsDataState>) => void;
	}
> = createStore({
	initialState: getInitialState(),
	actions,
	name: 'CommentsDataStore',
});

export const useCommentsData: HookFunction<
	CommentsDataState,
	BoundActions<
		CommentsDataState,
		{
			setOrderedActiveAnnotationIdList: (
				newCommentsList: string[],
			) => ({ getState, setState }: StoreActionApi<CommentsDataState>) => void;
			getCurrentCommentsPanelState: () => ({
				getState,
			}: StoreActionApi<CommentsDataState>) => Readonly<CommentsDataState>;
			// keep track of removed annotations
			updateRemovedCommentIdsMap: ({
				threadKey,
				commentId,
				commentActionType,
				isReply,
				commentType,
			}: {
				threadKey: string;
				commentId: string;
				commentActionType:
					| CommentActionType.DELETE_COMMENT
					| CommentActionType.RESOLVE_COMMENT_THREAD;
				isReply: boolean;
				commentType: CommentType;
			}) => ({ setState, getState }: StoreActionApi<CommentsDataState>) => void;
			reopenParentComment: ({
				threadKey,
				commentType,
				parentCommentId,
			}: {
				threadKey: string;
				commentType: CommentType;
				parentCommentId?: string;
			}) => ({ setState, getState }: StoreActionApi<CommentsDataState>) => void;
			clearRemovedComments: () => ({
				getState,
				setState,
			}: StoreActionApi<CommentsDataState>) => void;
			clearCommentsData: () => ({ setState }: StoreActionApi<CommentsDataState>) => void;
		}
	>,
	void
> = createHook(CommentsDataStore);
export const useCommentsDataActions: HookActionsFunction<
	BoundActions<
		CommentsDataState,
		{
			setOrderedActiveAnnotationIdList: (
				newCommentsList: string[],
			) => ({ getState, setState }: StoreActionApi<CommentsDataState>) => void;
			getCurrentCommentsPanelState: () => ({
				getState,
			}: StoreActionApi<CommentsDataState>) => Readonly<CommentsDataState>;
			// keep track of removed annotations
			updateRemovedCommentIdsMap: ({
				threadKey,
				commentId,
				commentActionType,
				isReply,
				commentType,
			}: {
				threadKey: string;
				commentId: string;
				commentActionType:
					| CommentActionType.DELETE_COMMENT
					| CommentActionType.RESOLVE_COMMENT_THREAD;
				isReply: boolean;
				commentType: CommentType;
			}) => ({ setState, getState }: StoreActionApi<CommentsDataState>) => void;
			reopenParentComment: ({
				threadKey,
				commentType,
				parentCommentId,
			}: {
				threadKey: string;
				commentType: CommentType;
				parentCommentId?: string;
			}) => ({ setState, getState }: StoreActionApi<CommentsDataState>) => void;
			clearRemovedComments: () => ({
				getState,
				setState,
			}: StoreActionApi<CommentsDataState>) => void;
			clearCommentsData: () => ({ setState }: StoreActionApi<CommentsDataState>) => void;
		}
	>
> = createActionsHook(CommentsDataStore);
export const useCommentsDataState: HookStateFunction<CommentsDataState, void> =
	createStateHook(CommentsDataStore);
